import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { ExplicitConfirmationComponent } from '@components/explicit-confirmation/explicit-confirmation.component';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SwapRtuProgressDialogComponent } from '../page/tank/components/tank-setup/widgets/swap-rtu-progress-dialog/swap-rtu-progress-dialog.component';
import { ConfirmationDialogComponent } from './../modules/confirmation-dialog/confirmation-dialog.component';
export const SaveMessages = {
  Saving: 'Saving...',
  SaveSuccess: 'Saved Successfully',
  SaveError: 'Save Failed',
};

@Injectable({
  providedIn: 'root',
})
export class AppDialogService {
  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  /**
   * Dialogs and snackbars are attached to the CDK overlay container, a div appended to <body> that
   * sits outside the <router-outlet>. Routing away therefore destroys the page behind them and
   * leaves them on screen - and a dialog opened with `disableClose` cannot even be dismissed by
   * hand. Anything that ends the session has to detach them explicitly; see
   * AuthService.processLogout.
   */
  dismissAllOverlays(): void {
    this.dialog.closeAll();
    this.snackBar.dismiss();
  }

  showProgressDialog(title: string, message: string): MatDialogRef<SwapRtuProgressDialogComponent> {
    return this.dialog.open(SwapRtuProgressDialogComponent, {
      disableClose: true,
      data: { title, message },
    });
  }

  showGeneralMessage(message: string, optConfig: object = {}): MatSnackBarRef<TextOnlySnackBar> {
    this.snackBar.dismiss();
    optConfig = {
      ...{
        duration: environment.snackbarDelayBeforeClose,
        panelClass: ['snackbar-generic'],
      },
      ...optConfig,
    };

    return this.snackBar.open(message, null, optConfig);
  }

  /**
   * A message that stays until the user deals with it, with a button they can press. Subscribe to
   * `onAction()` on the returned ref for the press.
   *
   * Persistent deliberately: this is for telling someone that what is on screen no longer matches
   * what the server will answer with. A message like that timing out and disappearing unread is
   * worse than not showing it, because the page then looks normal and is not.
   */
  showActionMessage(message: string, actionLabel: string): MatSnackBarRef<TextOnlySnackBar> {
    this.snackBar.dismiss();
    return this.snackBar.open(message, actionLabel, {
      duration: 0,
      panelClass: ['snackbar-generic'],
    });
  }

  showPersistentMessage(message: string): MatSnackBarRef<TextOnlySnackBar> {
    this.snackBar.dismiss();
    return this.snackBar.open(message, null, {
      duration: 0,
      panelClass: ['snackbar-generic'],
    });
  }

  showSuccessMessage(message: string) {
    this.snackBar.dismiss();

    this.snackBar.open(message, null, {
      duration: environment.snackbarDelayBeforeClose,
      panelClass: ['snackbar-success'],
    });
  }

  showErrorMessage(message: string, response?: any | null) {
    this.snackBar.dismiss();

    let duration = environment.snackbarDelayBeforeClose;
    let errors = [];

    //process the error response.
    // Model may change depending on the error type.
    // Attempt to handle all possible error responses.
    // Note: responseType 'text' means error bodies arrive as raw strings — parse them.
    let errorPayload = response?.error;
    if (typeof errorPayload === 'string') {
      try { errorPayload = JSON.parse(errorPayload); } catch { /* not JSON */ }
    }

    if (response && response.errors && response.errors.constructor === Array) {
      errors = response.errors;
    } else if (errorPayload && Array.isArray(errorPayload)) {
      errors = errorPayload;
    } else if (errorPayload && errorPayload.errors && Array.isArray(errorPayload.errors)) {
      errors = errorPayload.errors;
    }

    if (errors.length > 0) {
      const timestamperrorsExist = errors.find(
        (error: any) => error.errorNo >= 1 && error.errorNo <= 5
      );

      if (timestamperrorsExist) {
        duration = environment.snackbarDelayBeforeClose * 2;
        message += `\n ${this.translate.instant('General.TimestampError')}`;
      } else {
        errors.forEach((error: any) => {
          message += `\n ${this.translate.instant('Errors.' + error.stringResourceKey)}`;
        });
      }
    }

    this.snackBar.open(message, 'OK', {
      duration: duration,
      panelClass: ['snackbar-failure'],
    });
  }

  showConfirmationDialog(
    title: string,
    content: string,
    yesButton: string = 'Yes',
    noButton: string = 'No',
    yesButtonTemplate: TemplateRef<any> = null
  ): Observable<boolean> {
    return this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        data: {
          title,
          content,
          yesButtonLabel: yesButton,
          noButtonLabel: noButton,
          usersChoice: false,
          yesButtonTemplate: yesButtonTemplate,
        },
      })
      .afterClosed()
      .pipe(switchMap(result => of(result)));
  }

  showExcplicitConfirmationDialog(dialogOptions: {
    explicitConfirmationText: string;
    title: string;
    messages: {
      message: string;
      icon: string;
      color: string;
    }[];
    cancelButtonText: string;
    confirmButtonText: string;
    inputLabelSuffix: string;
    /**
     * Whether the strings above are translation keys. Defaults to true, which is what every
     * existing caller relies on. Pass false from a page that is deliberately not localised —
     * otherwise its literal English is treated as missing keys and rendered as `[brackets]`.
     */
    translateContent?: boolean;
  }): Observable<boolean> {
    const dialogRef = this.dialog.open(ExplicitConfirmationComponent, {
      panelClass: 'cso-dialog',
      data: {
        explicitConfirmationText: dialogOptions.explicitConfirmationText,
        title: dialogOptions.title,
        messages: dialogOptions.messages,
        cancelButtonText: dialogOptions.cancelButtonText,
        confirmButtonText: dialogOptions.confirmButtonText,
        inputLabelSuffix: dialogOptions.inputLabelSuffix,
        translateContent: dialogOptions.translateContent ?? true,
      },
    });
    return dialogRef.afterClosed().pipe(map((result: boolean) => result));
  }

  showDialog(component: any, data?: any): Observable<any> {
    return this.dialog
      .open(component, { panelClass: 'cso-dialog', data: data })
      .afterClosed()
      .pipe(switchMap(result => of(result)));
  }

  showDialogReturnRef(component: any, data?: any): MatDialogRef<any> {
    return this.dialog.open(component, {
      panelClass: 'cso-dialog',
      data: data,
    });
  }

  showPermissionsDialogReturnRef(component: any, data?: any): MatDialogRef<any> {
    const width = '70%';
    return this.dialog.open(component, {
      panelClass: 'cso-dialog',
      data: data,
      width : width,
    });
  }

  showGeneralDialog(title: string, content: string): Observable<any> {
    return this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        data: {
          title,
          content,
          yesButtonLabel: 'Ok',
          usersChoice: false,
          showNoButton: false,
          showYesButton: true,
        },
      })
      .afterClosed()
      .pipe(switchMap(() => of(null)));
  }
  showaddInventoryConfirmationDialog(dialogOptions: {
    explicitConfirmationText: string;
    title: string;
    messages: {
      message: string;
      icon: string;
      color: string;
    }[];
    cancelButtonText: string;
    confirmButtonText: string;
    inputLabelSuffix: string;
  }): MatDialogRef<ExplicitConfirmationComponent> {
  return this.dialog.open(ExplicitConfirmationComponent, {
    panelClass: 'cso-dialog',
    data: dialogOptions,
  });
}
}
